import { useState, useRef, useCallback, ReactNode, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useFloating, offset, flip, shift, size, autoUpdate } from '@floating-ui/react';
import { cn } from '@restworld/utils-common';

import { Spinner } from '../spinner';
import {
  selectLabelStyle,
  selectSecondaryLabelStyle,
  SelectMainWrapper,
  selectInteractiveAreaStyle,
  ArrowIconWrapper,
  selectInteractiveAreaContentStyle,
  selectOptionsStyle,
  selectOptionStyle,
  HelperTextVariantType,
  StyledSelectHelperText,
} from './select.helpers';

export function Combobox({
  value,
  onChange,
  dropdownList,
  placeholder,
  inputWrapperStyle,
  listStyle,
  inputStyle,
  noOptionsMessage,
  error,
  isLoading,
  onQueryChange,
  disabled,
  label,
  secondaryLabel,
  fullwidth,
  autoCompleteOff = true,
  showIcon = true,
  noFilter = false,
  readOnly = false,
  helperText: { variant: helperTextVariant, message: helperTextMessage } = { variant: 'error', message: '' },
}: ComboboxProps) {
  const { t: translateCommon } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles } = useFloating({
    strategy: 'fixed',
    placement: 'bottom',
    middleware: [
      offset(4),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const filteredOptions =
    query === '' || noFilter
      ? dropdownList
      : dropdownList?.filter((v: string) => v.toLowerCase().includes(query.toLowerCase()));

  // Close on outside click — check both wrapper and floating portal
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        (!refs.floating.current || !refs.floating.current.contains(target))
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, refs.floating]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setQuery(inputValue);
      if (inputValue === '') {
        onChange && onChange('');
      }
      setIsDropdownOpen(true);
    },
    [onChange, setQuery]
  );

  const handleSelectOption = useCallback(
    (option: string) => {
      onChange && onChange(option);
      setIsDropdownOpen(false);
    },
    [onChange]
  );

  const optionsListEl = useMemo(
    () =>
      isLoading ? (
        <div className="w-full flex items-center justify-center p-1">
          <Spinner color="primary" size="sm" className="mx-auto" />
        </div>
      ) : filteredOptions?.length ? (
        <ul ref={dropdownRef} role="listbox" className={cn(selectOptionsStyle, listStyle)}>
          {filteredOptions.map((v: string) => (
            <li
              key={v}
              role="option"
              aria-selected={value === v}
              // Use onMouseDown + preventDefault to avoid focus leaving the input
              // before the selection fires (prevents Radix Dialog from dismissing)
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectOption(v);
              }}
              className={cn(selectOptionStyle, 'flex items-center gap-2')}
            >
              <span className="truncate">{v}</span>
            </li>
          ))}
        </ul>
      ) : query ? (
        <ul ref={dropdownRef} role="listbox" className={cn(selectOptionsStyle, listStyle)}>
          <li className={cn(selectOptionStyle, 'text-neutral-200 cursor-default hover:bg-transparent')}>
            {noOptionsMessage || `-- ${translateCommon('no_options_loaded')} --`}
          </li>
        </ul>
      ) : null,
    [filteredOptions, isLoading, listStyle, noOptionsMessage, query, translateCommon, value, handleSelectOption]
  );

  useEffect(() => {
    onQueryChange && onQueryChange(query);
  }, [onQueryChange, query]);

  return (
    <SelectMainWrapper fullwidth={fullwidth}>
      <div ref={wrapperRef}>
        {label && (
          <label className={cn(selectLabelStyle, disabled && 'text-neutral-100')}>
            <span>{label}</span> <span className={selectSecondaryLabelStyle}>{secondaryLabel}</span>
          </label>
        )}
        <div className={cn('relative w-full', inputWrapperStyle)} ref={refs.setReference}>
          <input
            type="text"
            onClick={() => setIsDropdownOpen(true)}
            onChange={handleChange}
            value={isDropdownOpen ? query : value || ''}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            autoComplete={autoCompleteOff ? 'off' : undefined}
            role="combobox"
            aria-expanded={isDropdownOpen}
            className={cn(
              selectInteractiveAreaStyle,
              selectInteractiveAreaContentStyle,
              'truncate pr-12',
              'placeholder:text-base placeholder:font-normal placeholder:text-neutral-200',
              'active:border-neutral-100 active:outline-none active:ring-0',
              'focus:border-neutral-100 focus:outline-none focus:ring-0',
              disabled && 'bg-neutral-40 hover:bg-neutral-40 cursor-not-allowed',
              error && 'border-none outline-red-200 bg-red-50',
              inputStyle
            )}
          />
          {showIcon && (
            <ArrowIconWrapper isOpen={isDropdownOpen} className="absolute top-[13px] right-4">
              <ChevronDownIcon className="h-6 w-6 text-neutral-700" />
            </ArrowIconWrapper>
          )}
        </div>
        {isDropdownOpen &&
          typeof document !== 'undefined' &&
          ReactDOM.createPortal(
            <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 200, pointerEvents: 'auto' }}>
              {optionsListEl}
            </div>,
            document.body
          )}
      </div>
      {helperTextMessage && (
        <StyledSelectHelperText variant={helperTextVariant}>{helperTextMessage}</StyledSelectHelperText>
      )}
    </SelectMainWrapper>
  );
}
export default Combobox;

export interface ComboboxProps {
  value?: string;
  onChange?: (value: string) => void;
  dropdownList?: string[];
  placeholder?: string;
  inputWrapperStyle?: string;
  listStyle?: string;
  inputStyle?: string;
  showIcon?: boolean;
  noOptionsMessage?: ReactNode;
  error?: string;
  noFilter?: boolean;
  isLoading?: boolean;
  autoCompleteOff?: boolean;
  onQueryChange?: (query: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  helperText?: { variant?: HelperTextVariantType; message: string };
  label?: string;
  secondaryLabel?: string;
  fullwidth?: boolean;
}
